// Premium loading & skeleton primitives used across every page for a
// consistent luxury feel. No animation library needed — pure CSS keyframes
// scoped via Tailwind arbitrary values + a small inline style block.

const KEYFRAMES = `
@keyframes lux-spin { to { transform: rotate(360deg); } }
@keyframes lux-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes lux-page-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.lux-page-in { animation: lux-page-in 380ms cubic-bezier(0.22,0.61,0.36,1) both; }
`;

// Inject once — safe even if imported by multiple pages because IDs de-dupe.
if (typeof document !== "undefined" && !document.getElementById("lux-loader-keyframes")) {
  const el = document.createElement("style");
  el.id = "lux-loader-keyframes";
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

// ---------- Spinner ----------
export const LuxLoader = ({ label = "Loading", size = 42, block = true, testId = "lux-loader" }) => {
  const border = Math.max(2, Math.round(size / 14));
  const content = (
    <div className="flex flex-col items-center gap-3" data-testid={testId}>
      <div
        style={{
          width: size,
          height: size,
          border: `${border}px solid rgba(212,175,55,0.15)`,
          borderTopColor: "#D4AF37",
          borderRadius: "50%",
          animation: "lux-spin 720ms linear infinite",
          boxShadow: "0 0 20px rgba(212,175,55,0.15)",
        }}
      />
      {label && (
        <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-gray-400">
          {label}
        </span>
      )}
    </div>
  );
  if (!block) return content;
  return <div className="grid place-items-center px-6 py-16">{content}</div>;
};

// ---------- Skeleton bar (warm-ivory shimmer, matches Royal Lady palette) ----------
// Uses global .lux-shimmer class from index.css so it inherits
// prefers-reduced-motion fallback and Royal Lady's warm ivory tint.
export const LuxSkeleton = ({ className = "", ...rest }) => (
  <div
    aria-hidden
    {...rest}
    className={`lux-shimmer ${className}`}
    style={rest.style}
  />
);

// ---------- Single product card skeleton (matches ProductCard aspect + meta) ----------
// Exact dimensions to prevent Cumulative Layout Shift when real card renders.
export const LuxProductCardSkeleton = ({ testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <LuxSkeleton className="aspect-[3/4] w-full" />
    <LuxSkeleton className="h-3 w-3/4" />
    <LuxSkeleton className="h-3 w-1/3" />
    <LuxSkeleton className="h-2.5 w-2/5" />
  </div>
);

// ---------- Product-card grid skeleton ----------
export const LuxProductGridSkeleton = ({ count = 8, className = "" }) => (
  <div
    className={`grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 ${className}`}
    data-testid="lux-grid-skeleton"
  >
    {Array.from({ length: count }).map((_, i) => (
      <LuxProductCardSkeleton key={i} />
    ))}
  </div>
);

// ---------- Horizontal scroll rail skeleton (Home carousels) ----------
export const LuxProductRailSkeleton = ({ count = 6, testId = "lux-rail-skeleton" }) => (
  <div
    data-testid={testId}
    className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0"
  >
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="w-[168px] shrink-0 snap-start sm:w-[210px]">
        <LuxProductCardSkeleton />
      </div>
    ))}
  </div>
);

// ---------- Product Details Page skeleton ----------
// Two-column layout at md+; matches the real PDP structure so hero image slot,
// info block, size selector, and CTA row don't shift once data arrives.
export const LuxPDPSkeleton = ({ testId = "lux-pdp-skeleton" }) => (
  <div className="mx-auto max-w-screen-xl px-4 py-6 sm:px-6" data-testid={testId}>
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
      {/* Gallery */}
      <div className="space-y-3">
        <LuxSkeleton className="aspect-[4/5] w-full" />
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <LuxSkeleton key={i} className="aspect-square w-full" />
          ))}
        </div>
      </div>
      {/* Info */}
      <div className="space-y-4">
        <LuxSkeleton className="h-3 w-20" />
        <LuxSkeleton className="h-8 w-4/5" />
        <LuxSkeleton className="h-4 w-2/5" />
        <LuxSkeleton className="h-6 w-1/3" />
        <div className="pt-3">
          <LuxSkeleton className="h-3 w-16" />
          <div className="mt-2 flex gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <LuxSkeleton key={i} className="h-10 w-10" />
            ))}
          </div>
        </div>
        <div className="pt-2 flex gap-2">
          <LuxSkeleton className="h-12 flex-1" />
          <LuxSkeleton className="h-12 w-12" />
        </div>
        <div className="space-y-2 pt-4">
          <LuxSkeleton className="h-3 w-full" />
          <LuxSkeleton className="h-3 w-11/12" />
          <LuxSkeleton className="h-3 w-4/5" />
        </div>
      </div>
    </div>
  </div>
);

// ---------- Order card skeleton (Profile "My orders") ----------
export const LuxOrderCardSkeleton = () => (
  <div className="border border-gray-100 p-4">
    <div className="flex items-center justify-between">
      <LuxSkeleton className="h-3 w-24" />
      <LuxSkeleton className="h-3 w-16" />
    </div>
    <LuxSkeleton className="mt-3 h-4 w-1/2" />
    <LuxSkeleton className="mt-2 h-3 w-32" />
    <LuxSkeleton className="mt-4 h-7 w-24" />
  </div>
);

// ---------- Order tracking timeline skeleton ----------
export const LuxOrderTrackingSkeleton = ({ testId = "lux-tracking-skeleton" }) => (
  <div className="mx-auto max-w-screen-md px-4 py-6 sm:px-6" data-testid={testId}>
    <LuxSkeleton className="h-3 w-24" />
    <LuxSkeleton className="mt-2 h-6 w-2/3" />
    <div className="mt-8 space-y-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-start gap-4">
          <LuxSkeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <LuxSkeleton className="h-3 w-1/3" />
            <LuxSkeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
    <LuxSkeleton className="mt-8 h-28 w-full" />
  </div>
);

// ---------- Notification list item skeleton ----------
export const LuxNotificationSkeleton = ({ count = 4 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex gap-3 border border-gray-100 p-4">
        <LuxSkeleton className="h-9 w-9 rounded-full" />
        <div className="flex-1 space-y-2">
          <LuxSkeleton className="h-3 w-2/5" />
          <LuxSkeleton className="h-3 w-4/5" />
          <LuxSkeleton className="h-2.5 w-20" />
        </div>
      </div>
    ))}
  </div>
);

// ---------- Route-fade wrapper ----------
export const PageFade = ({ children, className = "" }) => (
  <div className={`lux-page-in ${className}`}>{children}</div>
);
